import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';

// Shared-rig contract (see the framing addendum). 'forbidden': this is an
// instrument reading, not a stage - no lights, no floor, no rig.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'phosphorscope',
  family: 'vector',
  energyLevel: 0.40,
  bpmMin: 70,
  bpmMax: 140,
  movement: 0.35,
  danceability: 0.50,
  tonality: 0.85,
  enabled: true
};

// PhosphorScope: a Lissajous figure traced like a beam on an analogue
// oscilloscope's phosphor screen, with a simulated afterglow. The calmest
// visualizer in the library, deliberately - every other piece here is
// something happening TO you (a stage, a crowd, a laser rig); this is
// something you watch happen, precisely, on its own schedule. It is meant to
// breathe.
//
// THE CENTRAL TRICK: integer frequency ratios close the curve and hold it
// still; a small detune opens it into a slow precession. That is exactly how
// a real analogue scope behaves when its two channels drift a fraction of a
// hertz apart, and it is what keeps a perfectly geometric figure from ever
// reading as static or looping.
//
// PERFORMANCE SHAPE (the lightest visualizer in the library - thin lines,
// almost no fill, but real vertex-shader work per point):
//  - ONE draw call per trace (an InstancedBufferGeometry: one quad template,
//    instanced once per curve segment), one for the graticule.
//  - ZERO per-frame attribute uploads. Every point's position, tangent and
//    screen-space extrusion are derived in the vertex shader from its own
//    instance index and a handful of uniforms; update() writes scalars only.
//  - mediump fragment precision, no texture, no discard on the curve itself
//    (the graticule's line mask is a smoothstep band, not a cutout).

const CAMERA_POS = [0, 0, 9];
const CAMERA_LOOK = [0, 0, 0];
// Fraction of the frame's half-height the curve's centre sits above screen
// centre - computed into world units from the camera above in init(), not
// hard-coded, so the framing survives if the camera numbers ever change.
const CENTER_OFFSET_FRAC = 0.08;
// How far past the frame's half-height the curve's own vertical extent must
// reach - see init()'s own framing comment for the arithmetic this backs.
const OVERFLOW_MARGIN = 1.25;

// Eight integer (x, y) ratio pairs. A ratio closes the figure only because
// EVERY frequency touching the curve is an integer multiple of the base
// angular step - see PRIMARY_AMP/SECONDARY_AMP below, and buildCurveChunk's
// own comment on why the secondary harmonic's base frequency is derived from
// the same pair rather than picked separately.
const RATIO_PRESETS = [
  [3, 2], [5, 4], [5, 3], [7, 4], [4, 3], [7, 5], [8, 5], [9, 7]
];
// The secondary harmonic per axis sits one integer step above the primary -
// enough to visibly thicken/texture the figure without fighting the primary
// ratio for the overall shape, and (critically) still an integer, so the
// closure guarantee holds for the WHOLE curve, not just its dominant term.
const SECONDARY_STEP = 1;
const PRIMARY_AMP = 1.0;
const SECONDARY_AMP = 0.35;
// Quarter-turn offset between the x and y primary phases - the textbook
// Lissajous relationship that turns a bare 1:1 ratio into a circle rather
// than a diagonal line, kept as the base for every preset.
const PHASE_X = 0;
const PHASE_Y = Math.PI / 2;
const DETUNE_MAX = 0.035;

const TIER_LAYOUT = {
  0: { traces: 2, points: 900, graticule: false },
  1: { traces: 2, points: 1400, graticule: true },
  2: { traces: 3, points: 1800, graticule: true }
};
const DEFAULT_TIER = 2;

// --- Trail / phosphor persistence -------------------------------------
// Not given a specific number by the brief - tuned so the trail has visibly
// decayed (to ~5% of peak) by a third of the way round the loop: a comet
// tail long enough to read as motion, short enough that most of the circle
// stays at the permanent-afterimage floor rather than washing out into a
// uniform ring.
const TRAIL_DECAY = 8.5;
const TRAIL_FLOOR = 0.12; // permanent afterimage - see the brief's own bright() formula
const TRAIL_WIDTH_PX = 2.5;
const HEAD_WIDTH_PX = 7.0;
const CORE_WIDTH_PX = 1.2; // the white-hot centre within the wider coloured ribbon
const HEAD_CORE_LUMINANCE = 0.95;
// How close to the head (in the same 0..1 curve-parameter units as `age`)
// the beat pulse is allowed to reach - the brief is explicit that the pulse
// belongs to the head alone, not the trail, so this needs its own much
// tighter falloff than TRAIL_DECAY's broader comet-tail shape.
const HEAD_PULSE_ZONE = 0.05;
const BEAT_PULSE_AMOUNT = 0.5;
const BEAT_PULSE_DECAY = 10;

const PEAK_LUMINANCE = 0.92;

// --- Beat/bar timing -----------------------------------------------------
const HEAD_BEATS_PER_LOOP = 2;
const REFERENCE_BPM = 120; // wall-clock fallback when bpm is unavailable - see criterion 5
const BEATS_PER_BAR = 4; // standard 4/4 assumption, same as orchestrator.js's own measureSeconds

// --- Graticule -----------------------------------------------------------
const GRATICULE_DIVISIONS = [10, 8];
const GRATICULE_LINE_OPACITY = 0.06;
const GRATICULE_AXIS_OPACITY = 0.14;
const GRATICULE_LINE_PX = 1;
const GRATICULE_SIZE = [24, 15]; // generous over-cover of the frame at this camera - see init()

const FALLBACK_COLORS = [0x2fd8ff, 0xff2fb0, 0xffb02f];

function hex3(c) {
  return new THREE.Color(c);
}

// Scaling a colour by one factor keeps its hue; per-channel clamping
// collapses a saturated colour toward white as soon as two channels hit the
// ceiling. Same convention as every other file in this codebase.
const CLAMP_CHUNK = `
  vec3 clampLuminance(vec3 col) {
    float mx = max(col.r, max(col.g, col.b));
    return mx > ${PEAK_LUMINANCE} ? col * (${PEAK_LUMINANCE} / mx) : col;
  }
`;

// The curve itself, shared verbatim by every trace's vertex shader (only the
// uniform VALUES differ per trace - preset, phase offset, colour). Frequencies
// are angular multiples of one full turn (s in 0..1 maps to one lap), so an
// integer fx/fy closes exactly - see the file-level comment on why that
// matters and RATIO_PRESETS' own comment on the secondary harmonic.
const CURVE_CHUNK = `
  uniform vec2 uFx; // (primary, secondary) integer multiples on x
  uniform vec2 uFy; // (primary, secondary) integer multiples on y
  uniform float uDetune;
  uniform float uPhaseOffset; // per-trace stagger, added to every phase term

  vec2 curvePoint(float s) {
    float ang = s * 6.283185307;
    float x = ${PRIMARY_AMP.toFixed(2)} * sin(uFx.x * ang + ${PHASE_X.toFixed(4)} + uPhaseOffset)
             + ${SECONDARY_AMP.toFixed(2)} * sin((uFx.y + uDetune) * ang + ${PHASE_X.toFixed(4)} + uPhaseOffset);
    float y = ${PRIMARY_AMP.toFixed(2)} * sin(uFy.x * ang + ${PHASE_Y.toFixed(4)} + uPhaseOffset)
             + ${SECONDARY_AMP.toFixed(2)} * sin((uFy.y + uDetune) * ang + ${PHASE_Y.toFixed(4)} + uPhaseOffset);
    return vec2(x, y);
  }
`;

function buildTraceVertexShader(pointCount) {
  return `
    precision highp float;

    // position.x = -1/+1 (which edge of the ribbon), position.y = 0/1 (which
    // end of this segment) - the SAME unit-quad-instanced-per-segment
    // technique laser-cathedral.js uses for its beams, just with both
    // endpoints derived from one procedural curve instead of a projector
    // origin/direction pair.
    attribute float iIndex;

    uniform float uScale;
    uniform vec2 uCenterOffset;
    uniform float uHead;
    uniform float uDecay;
    uniform float uPulse;
    uniform float uPixelScale;
    uniform vec3 uColor;

    varying vec3 vColor;
    varying float vBright;
    varying float vWidthPx;
    varying float vAcross;
    varying float vSafeAtt;

    ${CURVE_CHUNK}
    ${SAFE_ZONE_VERTEX_CHUNK}

    void main() {
      float pointCount = ${pointCount.toFixed(1)};
      float s0 = iIndex / pointCount;
      // s1 wraps past 1.0 on the last segment - curvePoint() never needs an
      // explicit modulo for that, because integer uFx/uFy make s=1.0 and
      // s=0.0 the exact same point already (that IS the closure condition).
      float s1 = (iIndex + 1.0) / pointCount;
      float s = mix(s0, s1, position.y);

      vec2 p2 = curvePoint(s) * uScale + uCenterOffset;

      // Centred finite difference for the tangent, same spirit as chrome-
      // flux.js's normal correction: two extra curve evaluations per vertex,
      // negligible next to the point count.
      float eps = 0.5 / pointCount;
      vec2 tangent = (curvePoint(s + eps) - curvePoint(s - eps)) * uScale;
      float tLen = length(tangent);
      vec2 dir = tLen > 0.00001 ? tangent / tLen : vec2(1.0, 0.0);
      vec2 perp = vec2(-dir.y, dir.x);

      // Trail brightness/width - exactly the brief's own bright() formula,
      // computed here (not just in the fragment shader) because the WIDTH
      // itself needs to reach the vertex stage to size the extrusion.
      float age = fract(s - uHead);
      float trail = exp(-age * uDecay);
      float bright = ${TRAIL_FLOOR.toFixed(2)} + ${(1 - TRAIL_FLOOR).toFixed(2)} * trail;
      float widthPx = mix(${TRAIL_WIDTH_PX.toFixed(2)}, ${HEAD_WIDTH_PX.toFixed(2)}, trail);

      // Beat pulse reaches only the immediate head, never the trail - a much
      // tighter falloff than the trail term's own broader comet-tail shape.
      float headWeight = 1.0 - smoothstep(0.0, ${HEAD_PULSE_ZONE.toFixed(3)}, age);
      bright *= mix(1.0, uPulse, headWeight);

      vec3 unextruded = vec3(p2, 0.0);
      vec4 mv = modelViewMatrix * vec4(unextruded, 1.0);
      float worldPerPx = max(-mv.z, 0.001) / uPixelScale;
      vec3 worldPos = unextruded + vec3(perp * (position.x * widthPx * 0.5 * worldPerPx), 0.0);

      vColor = uColor;
      vBright = bright;
      vWidthPx = widthPx;
      vAcross = position.x;

      vec4 clip = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
      gl_Position = clip;
      vSafeAtt = safeZoneAttenuation(clip);
    }
  `;
}

const traceFragmentShader = `
  precision mediump float;

  varying vec3 vColor;
  varying float vBright;
  varying float vWidthPx;
  varying float vAcross;
  varying float vSafeAtt;

  ${CLAMP_CHUNK}

  void main() {
    float coreFrac = clamp(${CORE_WIDTH_PX.toFixed(2)} / max(vWidthPx, 0.5), 0.0, 0.9);
    float across = abs(vAcross);
    float core = 1.0 - smoothstep(0.0, coreFrac, across);
    float halo = 1.0 - across; // linear falloff across the ribbon's own width

    vec3 hot = mix(vColor, vec3(1.0), ${HEAD_CORE_LUMINANCE.toFixed(2)});
    vec3 col = mix(vColor * halo, hot, core) * vBright * vSafeAtt;
    gl_FragColor = vec4(clampLuminance(col), 1.0);
  }
`;

// A single quad, a trivial fragment shader: fract() against the division
// count gives the grid, two more bands right at the centre give the axes.
// One texture-free draw call, additive so it only ever brightens the black
// it sits on rather than needing its own transparency/depth ordering.
const graticuleVertexShader = `
  precision highp float;
  varying vec2 vUv;
  varying float vSafeAtt;
  ${SAFE_ZONE_VERTEX_CHUNK}
  void main() {
    vUv = uv;
    vec4 clip = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    gl_Position = clip;
    vSafeAtt = safeZoneAttenuation(clip);
  }
`;

const graticuleFragmentShader = `
  precision mediump float;
  uniform vec2 uDivisions;
  uniform float uLineWidthU;
  uniform float uLineWidthV;
  varying vec2 vUv;
  varying float vSafeAtt;

  float lineMask(float coord, float divisions, float halfWidth) {
    float cell = fract(coord * divisions);
    float d = min(cell, 1.0 - cell) / divisions;
    return 1.0 - smoothstep(0.0, halfWidth, d);
  }

  void main() {
    float gx = lineMask(vUv.x, uDivisions.x, uLineWidthU);
    float gy = lineMask(vUv.y, uDivisions.y, uLineWidthV);
    float grid = max(gx, gy);

    float ax = 1.0 - smoothstep(0.0, uLineWidthU, abs(vUv.x - 0.5));
    float ay = 1.0 - smoothstep(0.0, uLineWidthV, abs(vUv.y - 0.5));
    float axis = max(ax, ay);

    float brightness = grid * ${GRATICULE_LINE_OPACITY.toFixed(3)}
                      + axis * ${GRATICULE_AXIS_OPACITY.toFixed(3)};
    vec3 col = vec3(brightness) * vSafeAtt;
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class PhosphorScopeVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    camera.position.set(CAMERA_POS[0], CAMERA_POS[1], CAMERA_POS[2]);
    camera.lookAt(CAMERA_LOOK[0], CAMERA_LOOK[1], CAMERA_LOOK[2]);
    camera.updateMatrixWorld();
    camera.updateProjectionMatrix();

    // Frame half-height/width at the curve's own depth (z=0, camera on-axis
    // at CAMERA_POS[2]) - measured from the camera's real fov rather than
    // assumed, so this keeps working if the camera numbers ever change.
    const dist = Math.abs(CAMERA_POS[2] - CAMERA_LOOK[2]);
    const fovRad = THREE.MathUtils.degToRad(camera.fov);
    const halfH = dist * Math.tan(fovRad / 2);
    // Curve's own normalized half-extent on EITHER axis (both symmetric by
    // construction): the two sine terms' amplitudes summed, the worst case a
    // Lissajous with these components actually reaches somewhere on its lap.
    const normExtent = PRIMARY_AMP + SECONDARY_AMP;
    this._curveScale = (halfH * OVERFLOW_MARGIN) / normExtent;
    this._centerOffsetY = halfH * CENTER_OFFSET_FRAC;

    const layout = TIER_LAYOUT[this._tier];
    this._layout = layout;

    this._sharedUniforms = Object.assign({}, safeZoneUniforms, {
      uScale: { value: this._curveScale },
      uCenterOffset: { value: new THREE.Vector2(0, this._centerOffsetY) },
      uHead: { value: 0 },
      uDecay: { value: TRAIL_DECAY },
      uDetune: { value: 0 },
      uPulse: { value: 1 },
      uPixelScale: { value: 1 }
    });

    this._buildGeometry(layout.points);
    this._traces = [];
    for (let i = 0; i < layout.traces; i++) this._traces.push(this._buildTrace(i, layout.traces));
    if (layout.graticule) this._buildGraticule();

    this._time = 0;
    this._wallHeadTurns = 0;
    this._wallBarTimer = 0;
    this._head = 0;
    this._presetOffset = 0;
    this._lastBarBeatIndex = null;
    this._lastPaletteRef = null;
    this._applyPalette(null);
  }

  // One quad template (position.x = side, position.y = which endpoint),
  // instanced once per curve segment - see buildTraceVertexShader's own
  // comment. Shared by every trace: "une seule geometrie, trois instances"
  // means three MESHES pointing at this one geometry, not three copies of it.
  _buildGeometry(pointCount) {
    const quad = new Float32Array([
      -1, 0, 0, 1, 0, 0, 1, 1, 0, -1, 1, 0
    ]);
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    geometry.setAttribute('position', new THREE.BufferAttribute(quad, 3));
    const indices = new Float32Array(pointCount);
    for (let i = 0; i < pointCount; i++) indices[i] = i;
    geometry.setAttribute('iIndex', new THREE.InstancedBufferAttribute(indices, 1));
    geometry.instanceCount = pointCount;
    // Placement is entirely shader-side and the curve is scaled to overflow
    // the frame on purpose, so the mesh's own bounds are meaningless to the
    // frustum culler.
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 60);
    this._geometry = geometry;
  }

  _buildTrace(index, total) {
    const uniforms = Object.assign({}, this._sharedUniforms, {
      uFx: { value: new THREE.Vector2(3, 4) },
      uFy: { value: new THREE.Vector2(2, 3) },
      // uDetune deliberately NOT redeclared here - it comes from
      // _sharedUniforms via the Object.assign above, so writing
      // this._sharedUniforms.uDetune.value once in update() reaches every
      // trace's shader at once. Redeclaring it per-trace (as this file did
      // at first) shadows the shared holder with an independent one that
      // update()'s single write never touches - the bug that surfaced as
      // "Cannot set properties of undefined" the moment update() ran.
      // Evenly staggered around a full turn, so three overlapping traces of
      // similar-shaped presets read as offset copies rather than unrelated
      // clutter - "decalees en phase".
      uPhaseOffset: { value: (index / total) * Math.PI * 2 },
      uColor: { value: new THREE.Color(FALLBACK_COLORS[index % FALLBACK_COLORS.length]) }
    });
    const material = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: buildTraceVertexShader(this._layout.points),
      fragmentShader: traceFragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending
    });
    const mesh = new THREE.Mesh(this._geometry, material);
    mesh.frustumCulled = false;
    this._scene.add(mesh);
    return { uniforms, material, mesh, presetIndex: index % RATIO_PRESETS.length };
  }

  _buildGraticule() {
    const geometry = new THREE.PlaneGeometry(GRATICULE_SIZE[0], GRATICULE_SIZE[1]);
    this._graticuleUniforms = Object.assign({}, safeZoneUniforms, {
      uDivisions: { value: new THREE.Vector2(GRATICULE_DIVISIONS[0], GRATICULE_DIVISIONS[1]) },
      uLineWidthU: { value: 0.002 },
      uLineWidthV: { value: 0.002 }
    });
    this._graticuleMaterial = new THREE.ShaderMaterial({
      uniforms: this._graticuleUniforms,
      vertexShader: graticuleVertexShader,
      fragmentShader: graticuleFragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending
    });
    this._graticule = new THREE.Mesh(geometry, this._graticuleMaterial);
    this._graticule.position.set(0, 0, -0.2);
    this._graticule.renderOrder = -1;
    this._scene.add(this._graticule);
    this._graticuleGeometry = geometry;
  }

  // Applies the current preset (staggered per trace, advanced as one shared
  // step - see _advancePreset) to each trace's uFx/uFy.
  _applyPresets() {
    for (let i = 0; i < this._traces.length; i++) {
      const t = this._traces[i];
      const idx = (this._presetOffset + i) % RATIO_PRESETS.length;
      const [n, m] = RATIO_PRESETS[idx];
      t.uniforms.uFx.value.set(n, n + SECONDARY_STEP);
      t.uniforms.uFy.value.set(m, m + SECONDARY_STEP);
    }
  }

  _applyPalette(palette) {
    const vivid = (palette && palette.vividColors) || [];
    for (let i = 0; i < this._traces.length; i++) {
      const hex = vivid.length > 0 ? vivid[i % vivid.length] : FALLBACK_COLORS[i % FALLBACK_COLORS.length];
      this._traces[i].uniforms.uColor.value.set(hex);
    }
    this._applyPresets();
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    if (!paused) this._time += deltaTime;

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile.artworkPalette);
    }

    if (!paused) {
      if (profile.beatIndex != null && profile.beatPhase != null) {
        // Driven from the ABSOLUTE beat position scene-manager already
        // extrapolates (see sonos-visual-profile.js), not a locally
        // integrated clock - this is what guarantees the head lands back on
        // its start point exactly on the beat rather than merely close to
        // it, however long the track runs.
        const totalBeats = profile.beatIndex + profile.beatPhase;
        this._head = (totalBeats / HEAD_BEATS_PER_LOOP) % 1;

        const beatIndex = profile.beatIndex;
        if (beatIndex % BEATS_PER_BAR === 0 && beatIndex !== this._lastBarBeatIndex) {
          this._lastBarBeatIndex = beatIndex;
          this._presetOffset = (this._presetOffset + 1) % RATIO_PRESETS.length;
          this._applyPresets();
        }
      } else {
        // No BPM - wall-clock fallback at a reference tempo, same
        // convention every other visualizer in this library uses, so the
        // figure still loops and still changes preset on a plausible
        // cadence instead of freezing outright.
        const loopSeconds = (60 / REFERENCE_BPM) * HEAD_BEATS_PER_LOOP;
        this._wallHeadTurns += deltaTime / loopSeconds;
        this._head = this._wallHeadTurns % 1;

        const barSeconds = (60 / REFERENCE_BPM) * BEATS_PER_BAR;
        this._wallBarTimer += deltaTime;
        if (this._wallBarTimer >= barSeconds) {
          this._wallBarTimer -= barSeconds;
          this._presetOffset = (this._presetOffset + 1) % RATIO_PRESETS.length;
          this._applyPresets();
        }
        this._lastBarBeatIndex = null; // resume rising-edge detection cleanly if bpm returns
      }
    }
    // Paused: this._head simply keeps its last value - "gel de uHead".
    this._sharedUniforms.uHead.value = this._head;

    // Rises from 0 at the very start of the track to DETUNE_MAX mid-track
    // and back to 0 at the end - the same arch shape used across this whole
    // library for a "slow -> intense -> slow" arc, here read backwards as
    // "ordered -> drifting -> ordered".
    const detuneT = profile.duration > 0
      ? Math.sin(Math.PI * THREE.MathUtils.clamp(profile.progress, 0, 1))
      : 0.6;
    this._sharedUniforms.uDetune.value = DETUNE_MAX * detuneT;

    this._sharedUniforms.uPulse.value = profile.director.pulse(BEAT_PULSE_AMOUNT, BEAT_PULSE_DECAY);

    const canvasPixelHeight = window.innerHeight * Math.min(window.devicePixelRatio || 1, 2);
    const fovRadians = THREE.MathUtils.degToRad(this._camera.fov);
    this._sharedUniforms.uPixelScale.value = canvasPixelHeight / (2 * Math.tan(fovRadians / 2));

    if (this._graticuleUniforms) {
      // 1px lines on a flat quad facing the camera head-on: the quad's own
      // world size and the current pixel scale are enough to convert a pixel
      // width straight into UV units, no per-frame resize hook needed.
      const dist = Math.abs(this._camera.position.z - this._graticule.position.z);
      const worldPerPx = dist / this._sharedUniforms.uPixelScale.value;
      this._graticuleUniforms.uLineWidthU.value = (GRATICULE_LINE_PX * worldPerPx) / GRATICULE_SIZE[0];
      this._graticuleUniforms.uLineWidthV.value = (GRATICULE_LINE_PX * worldPerPx) / GRATICULE_SIZE[1];
    }
  }

  dispose() {
    for (const t of this._traces) {
      this._scene.remove(t.mesh);
      t.material.dispose();
    }
    this._geometry.dispose();
    if (this._graticule) {
      this._scene.remove(this._graticule);
      this._graticuleGeometry.dispose();
      this._graticuleMaterial.dispose();
    }
  }
}
